import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createDistrict, createInvitation } from "../lib/api";
import type {
  District,
  Godown,
  Invitation,
  PortalUser,
  Report,
} from "../lib/api";
import {
  DASH,
  districtHeadUser,
  fullName,
  isPass,
  stateScopeId,
  useSHDistricts,
  useSHGodowns,
  useSHInvitations,
  useSHReports,
  useSHState,
  useSHUsers,
} from "./data";
import { Tray } from "./Tray";

/** Everything the ledger needs for one district, from real data. */
function rowData(
  d: District,
  godowns: Godown[],
  reports: Report[],
  invites: Invitation[],
  users: PortalUser[],
) {
  const gs = godowns.filter((g) => g.district_id === d.id);
  const places = new Set(
    gs.map((g) => (g.place || "").trim()).filter(Boolean),
  );
  const gids = new Set(gs.map((g) => g.id));
  const rs = reports.filter((r) => r.godown_id != null && gids.has(r.godown_id));
  const passed = rs.filter((r) => isPass(r.verdict)).length;
  const dinv = invites.filter(
    (i) =>
      i.target_scope_type === "district" &&
      i.target_scope_id === d.id &&
      i.role === "district_head",
  );
  const accepted = dinv.find((i) => i.accepted_at);
  const pending = dinv.find((i) => !i.accepted_at && !i.revoked_at);
  const head = accepted || pending || null;
  const status = accepted ? "live" : pending ? "pending" : "setup";
  // Prefer the real name once the head has confirmed a profile; the
  // invite only ever carries the email.
  const headUser = districtHeadUser(users, d.id);
  const headLabel = (headUser && fullName(headUser)) || head?.email || null;
  return {
    places: places.size,
    godowns: gs.length,
    passed,
    total: rs.length,
    head,
    headLabel,
    status,
  };
}

const STATUS_TAG: Record<string, [string, string]> = {
  live: ["tag-live", "Live"],
  pending: ["tag-setup", "Awaiting confirmation"],
  setup: ["tag-off", "No head yet"],
};

export function Districts() {
  const navigate = useNavigate();
  const stateQ = useSHState();
  const districtsQ = useSHDistricts();
  const godownsQ = useSHGodowns();
  const reportsQ = useSHReports();
  const invitesQ = useSHInvitations();
  const usersQ = useSHUsers();
  const [drawer, setDrawer] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const state = stateQ.data?.state;
  const districts = districtsQ.data?.districts ?? [];
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];
  const invites = invitesQ.data?.invitations ?? [];
  const users = usersQ.data?.users ?? [];

  const say = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  };

  return (
    <>
      <p className="crumb">
        {(state?.name ?? "STATE").toUpperCase()} / DISTRICTS
      </p>
      <div className="page-head">
        <div>
          <h1>Districts</h1>
          <p>
            You add districts and their head. Each head adds their own godowns —
            open a district to see what they've set up.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-gold" onClick={() => setDrawer(true)}>
            Add district
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="ledger-head">
          <span>DISTRICT</span>
          <span>DISTRICT HEAD</span>
          <span>PLACES / GODOWNS</span>
          <span>REPORTS PASSED / FILED</span>
          <span>STATUS</span>
          <span />
        </div>

        {districtsQ.isLoading && (
          <div style={{ padding: 18 }} className="tray-cap">
            Loading…
          </div>
        )}
        {!districtsQ.isLoading && districts.length === 0 && (
          <div style={{ padding: 22 }}>
            <div className="empty" style={{ border: 0 }}>
              <b>No districts yet</b>
              <p>Add your first district and invite its head.</p>
              <button className="btn btn-gold" onClick={() => setDrawer(true)}>
                Add district
              </button>
            </div>
          </div>
        )}

        {districts.map((d) => {
          const r = rowData(d, godowns, reports, invites, users);
          const [cls, label] = STATUS_TAG[r.status];
          return (
            <button
              key={d.id}
              className="drow"
              onClick={() => navigate(`/sh/district/${d.id}`)}
            >
              <span>
                <span className="dname">{d.name}</span>
                <span className="dsub">
                  {d.code || `DISTRICT #${d.id}`}
                  {d.zone ? ` · ${d.zone.toUpperCase()} ZONE` : ""}
                </span>
              </span>
              <span className="owner hide-sm">
                {r.headLabel ?? DASH}
                <span>
                  {r.status === "live"
                    ? "confirmed"
                    : r.status === "pending"
                      ? "invite pending"
                      : "not invited"}
                </span>
              </span>
              <span className="num hide-sm">
                {r.godowns ? `${r.places} / ${r.godowns}` : DASH}
              </span>
              <span className="hide-sm">
                {r.total > 0 ? (
                  <Tray passed={r.passed} total={r.total} />
                ) : r.status === "pending" ? (
                  <span className="tray-cap" style={{ margin: 0 }}>
                    Grading starts after setup
                  </span>
                ) : (
                  <span className="tray-cap" style={{ margin: 0 }}>
                    {DASH}
                  </span>
                )}
              </span>
              <span className="hide-sm">
                <span className={"tag " + cls}>
                  <span className="dot" />
                  {label}
                </span>
              </span>
              <svg className="chev" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 3.5L10.5 8 6 12.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          );
        })}
      </div>

      <AddDistrictDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        onDone={(msg) => {
          setDrawer(false);
          say(msg);
        }}
      />

      <div className={"toast" + (toast ? " show" : "")}>
        <span className="dot" />
        <span>{toast}</span>
      </div>
    </>
  );
}

function AddDistrictDrawer({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [zone, setZone] = useState("");
  const [email, setEmail] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const stateId = stateScopeId();
      if (stateId == null) throw new Error("No state on your account.");
      const dres = await createDistrict({
        state_id: stateId,
        name: name.trim(),
        code: code.trim() || undefined,
        zone: zone.trim() || undefined,
      });
      const districtId = dres.district.id;
      await createInvitation({
        email: email.trim(),
        role: "district_head",
        target_scope_type: "district",
        target_scope_id: districtId,
      });
      return name.trim();
    },
    onSuccess: (savedName) => {
      qc.invalidateQueries({ queryKey: ["sh-districts"] });
      qc.invalidateQueries({ queryKey: ["sh-invitations"] });
      setName("");
      setCode("");
      setZone("");
      setEmail("");
      onDone(`${savedName} added. Invite sent to the district head.`);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <>
      <div
        className={"scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={"drawer" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-head">
          <div>
            <h2>Add a district</h2>
            <p>
              You add the district and its head. They add the godowns once they
              confirm their profile.
            </p>
          </div>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={onSubmit} style={{ display: "contents" }}>
          <div className="drawer-body">
            <span className="step">DISTRICT</span>
            <div className="grid-2">
              <div className="field">
                <label>District name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Saharsa"
                  autoFocus
                />
              </div>
              <div className="field">
                <label>
                  District code<span className="hint">optional</span>
                </label>
                <input
                  className="mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="BR-SHR"
                />
              </div>
            </div>
            <div className="field">
              <label>
                Zone<span className="hint">optional</span>
              </label>
              <input
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                placeholder="Koshi"
              />
            </div>
            <span className="step">DISTRICT HEAD</span>
            <div className="field">
              <label>
                Work email
                <span className="hint">the invite goes here</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div className="notice">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 4.5h12v8H2v-8z" stroke="currentColor" strokeWidth="1.4" />
                <path d="M2 5l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
              <p>
                <b>What happens next</b>
                AgriLens emails the district head an invite to set a password and
                join. Until they accept, the district shows as awaiting
                confirmation and has no godowns.
              </p>
            </div>
            {mut.isError && (
              <div
                className="notice"
                style={{ background: "#FDE8E8", borderColor: "#F5B5B5" }}
              >
                <p style={{ color: "#B00020" }}>
                  {(mut.error as Error).message}
                </p>
              </div>
            )}
          </div>
          <div className="drawer-foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={mut.isPending}
            >
              {mut.isPending ? "Saving…" : "Save and send invite"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
